import React, { useContext, useEffect } from 'react'
import Layout from 'components/layout'
import SEO from 'components/seo'
import { graphql } from 'gatsby'
import { injectIntl } from 'gatsby-plugin-intl'
import styled from 'styled-components'
import { colors, breakpoints, richTextOptions as options } from 'utils/utils'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import 'animate.css/animate.min.css'
import ScrollAnimation from 'react-animate-on-scroll'
import BackgroundImage from 'gatsby-background-image'
import HeroContainer from 'components/heroContainer'
import { GlobalStateContext } from '../context/globalContext'
import { GlobalDispatchContext } from '../context/globalContext'

export default injectIntl(props => {

    const state = useContext(GlobalStateContext)
    const dispatch = useContext(GlobalDispatchContext)  
     
    useEffect(() => {
        if(state.otherLanguageSlug !== props.pageContext.otherLanguageSlug) {
            dispatch(state.changeOtherLanguageSlug(props.pageContext.otherLanguageSlug))
        }
    }, [state, dispatch, props.pageContext.otherLanguageSlug])

    const { data, seo, title, cta, listBlockSection, media, jobOffers } = props.data.contentfulSection

    const bottomImage = media[0].fluid

    return (
        < >
            <Layout>
                <SEO 
                    title={seo.title}
                    keywords={seo.attributes[0].keywords}
                    slug={seo.slug}
                    description={seo.attributes[0].description}
                    lang={props.pageContext.language}
                />
                <Container>
                    <HeroContainer>
                        <Title>
                            <h1>
                                <ScrollAnimation animateOnce={true} animateIn="fadeInUp">
                                    {title}
                                </ScrollAnimation>
                            </h1>
                        </Title>
                        <Summary className="heroText" animateOnce={true} animateIn="fadeIn">
                            <div>
                                <span>{data.summaryText}</span>
                            </div>
                        </Summary>
                    </HeroContainer>
                    <CenterBlock>
                        <CenterBlockTitle>
                            <h2>
                                <ScrollAnimation animateOnce={true} animateIn="fadeInRight">
                                    {listBlockSection[0].title}
                                    <LinkCTA href={cta[0].href} target="_blank" rel="noreferrer">{cta[0].value}</LinkCTA>
                                </ScrollAnimation>
                            </h2>
                        </CenterBlockTitle>
                        <CenterBlockText>
                            <ScrollAnimation animateOnce={true} animateIn="fadeInLeft">
                                { documentToReactComponents(listBlockSection[0].text && listBlockSection[0].text.json, options) }
                            </ScrollAnimation>
                        </CenterBlockText>
                    </CenterBlock>
                    <BottomBlock>
                        <BottomBlockImage>
                            <BackgroundImage fluid={bottomImage} />
                        </BottomBlockImage>
                        <BottomBlockText animateOnce={true} animateIn="fadeIn">
                            <ScrollAnimation animateOnce={true} animateIn="fadeInUp">
                                <span>{data.bottomText}</span>
                                <BottomBlockButtons id="currentOffers">
                                    {
                                        jobOffers.map((jobOffer, index) => {
                                            return <LinkCTA key={index} href={jobOffer.offer ? jobOffer.offer.file.url : jobOffer.directLink} target="_blank" rel="noreferrer">{jobOffer.buttonText}</LinkCTA>
                                        })
                                    }
                                </BottomBlockButtons>
                            </ScrollAnimation>
                        </BottomBlockText>
                    </BottomBlock>     
                </Container>
            </Layout>
        </>
    )
})

const LinkCTA = styled.a`
    display: block;
    margin: 1rem auto;
    width: auto;
    min-width: 22rem;
    max-width: 22rem;
    padding: 1rem 2rem;
    background-color: ${colors.quaternary};
    color: ${colors.tertiary};
    text-decoration: none;
    font-size: 1rem;
    font-family: Avenir, sans-serif;
    text-transform: uppercase;
    ${breakpoints.md} {
        margin: 1rem auto;
    }
`

const BottomBlockButtons = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-evenly;
    margin-top: 1rem;
`

const BottomBlock = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    min-height: 30rem;
    ${breakpoints.md} {
        flex-direction: column;
    }

`
const BottomBlockImage = styled.div`
    width: 50%;
    min-height: 30rem;
    div {
        height: 100%;
    }
    ${breakpoints.md} {
        width: 100%;
        height: 30rem;
    }
`
const BottomBlockText = styled.div`
    padding: 3rem;
    background-color: ${colors.primary};
    color: ${colors.tertiary};
    width: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 30rem;
    text-align: center;
    > div {
        max-width: 60%;
        margin: auto;
        white-space: pre-wrap;
        ${breakpoints.md}{
            max-width: 80%;
            padding: 6vh 0;
        }
        a {
            display: block;
            margin: 1rem auto;
            width: fit-content;
            padding: 1rem 2rem;
            background-color: ${colors.quaternary};
            color: ${colors.tertiary};
            text-decoration: none;
        }
    }
    ${breakpoints.md}{
        position: initial;
        height: auto;
        width: 100%;
    }
`

const CenterBlock = styled.section`
    display: flex;
    width: 100%;
    padding: 3rem 0;

    ${breakpoints.md} {
        flex-direction: column;
    }    
`

const CenterBlockTitle = styled.div`
    width: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    ${breakpoints.md} {
        width: 100%;
        min-height: 10rem;
    }
    h2 {
        max-width: 22rem;
        margin: 10rem 3rem auto auto;
        font-size 3.5rem;
        text-align: right;
        ${breakpoints.md} {
            margin: auto;
            max-width: 90%;
            text-align: center;
            font-size 2.5rem;
        }
        a {
            margin: 1rem 0 1rem auto;
            width: fit-content;
            min-width: unset;
            max-width: unset;
            ${breakpoints.md} {
                margin: 1rem auto;
            }
        }
    }
`

const CenterBlockText = styled.div`    
    width: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    overflow: hidden;
    div {
        max-width: 60%;
        margin: auto auto auto 3rem;
        ${breakpoints.md}{
            max-width: 80%;
            padding: 6vh 0;
        }
        h4 {
            font-size: 1.3rem;
            font-weight: 500;
            margin: 1.5rem 0 0 0;
            color: ${colors.secondary};
        }
        p {
            font-size: 1rem;
        }
    }
    ${breakpoints.md}{
        position: initial;
        height: auto;
        width: 100%;
    }
`

const Container = styled.div`
    
`

const Title = styled.div`
    width: 50%;
    background-color: ${colors.primary};
    display: flex;
    justify-content: center;
    align-items: center;
    ${breakpoints.md} {
        width: 100%;
        min-height: 30rem;
    }
    h1 {
        font-size: 4rem;
        color: ${colors.tertiary};
        max-width: 70%;
        margin: auto;
        font-size 3.5rem;
        ${breakpoints.md} {
            margin: auto;
            max-width: 90%;
            text-align: center;
            font-size 2.5rem;
        }
    }
`

const Summary = styled(ScrollAnimation)`
    background-color: ${colors.quinary};
    width: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    // height: 100%;
    line-height: 1.5;
    div {
        max-width: 60%;
        margin: auto;
        white-space: pre-wrap;
        ${breakpoints.md}{
            max-width: 80%;
            padding: 6vh 0;
        }
    }
    ${breakpoints.md}{
        position: initial;
        height: auto;
        width: 100%;
    }
`

export const query = graphql`
    query($language: String) {
        contentfulSection(name: {eq: "Carrera"}, node_locale: {eq: $language}) {
            id
            title
            seo {
                title
                slug
                attributes {
                    description
                    keywords
                }
            }
            data {
                summaryText
                bottomText
            }
            cta {
                id
                href
                value
            }
            media {
                title
                fluid(quality : 100){
                    ...GatsbyContentfulFluid_withWebp_noBase64
                }
            }
            listBlockSection {
                title
                text {
                    json
                }
            }
            jobOffers {
                buttonText
                offer {
                    file {
                        url
                    }
                }
                directLink
            }
        }
    }

`
